// Logo generado con el nombre del bar mientras no suba uno propio.
// Puro (sin DOM): estima anchos con métricas aproximadas de Bebas Neue para
// repartir el nombre en 1–3 líneas sin desbordar los 500 de ancho del lienzo.

export const LOGO_CANVAS_WIDTH = 500
export const LOGO_MAX_TEXT_WIDTH = 380
export const LOGO_MIN_FONT_SIZE = 34
export const LOGO_MAX_LINES = 3
export const LOGO_FALLBACK_NAME = 'MI BAR'

// Tamaño máximo por número de líneas: así el bloque no domina el
// conjunto (cubiertos + nombre + filetes) y queda proporcionado.
const MAX_FONT_SIZE_BY_LINES = [150, 105, 70]
export const LOGO_LINE_HEIGHT = 0.95

const NARROW_CHARS = new Set(['I', '1', 'J', '.', ',', ':', ';', '\'', '’', '!', '|', '¡', '-'])
const WIDE_CHARS = new Set(['M', 'W', '&', '@', 'Æ', 'Œ'])

// Ancho en em de un carácter ya en mayúsculas.
const charWidth = (char: string) => {
  if (char === ' ') return 0.2
  if (NARROW_CHARS.has(char)) return 0.2
  if (WIDE_CHARS.has(char)) return 0.53
  if (char.length > 1 || char.codePointAt(0)! > 0x2000) return 1 // emojis y símbolos
  return 0.4
}

export const textWidthEm = (text: string) => Array.from(text).reduce((sum, char) => sum + charWidth(char), 0)

export interface LogoNameLayout {
  lines: string[]
  fontSize: number
  lineHeight: number
  /** Ancho estimado de cada línea en unidades del viewBox (≤ LOGO_MAX_TEXT_WIDTH). */
  lineWidths: number[]
}

export const normalizeLogoName = (name?: string | null) => {
  const normalized = (name ?? '').replace(/\s+/g, ' ').trim().toLocaleUpperCase('es')
  return normalized || LOGO_FALLBACK_NAME
}

const fontSizeFor = (lines: string[]) => {
  const widest = Math.max(...lines.map(textWidthEm))
  return Math.min(MAX_FONT_SIZE_BY_LINES[lines.length - 1], LOGO_MAX_TEXT_WIDTH / widest)
}

// Reparto contiguo de palabras en n líneas que minimiza la línea más ancha.
const balancedSplit = (words: string[], n: number): string[] => {
  if (n === 1) return [words.join(' ')]
  let best: string[] = []
  let bestWidth = Infinity
  for (let i = 1; i <= words.length - n + 1; i++) {
    const head = words.slice(0, i).join(' ')
    const rest = balancedSplit(words.slice(i), n - 1)
    const width = Math.max(textWidthEm(head), ...rest.map(textWidthEm))
    if (width < bestWidth) {
      bestWidth = width
      best = [head, ...rest]
    }
  }
  return best
}

// Trocea una palabra que no cabe ni a tamaño mínimo en piezas parejas con guion.
const hyphenate = (word: string, maxEm: number): string[] => {
  const chars = Array.from(word)
  let count = Math.ceil(textWidthEm(word) / (maxEm - textWidthEm('-')))
  let pieces: string[] = []
  do {
    const size = Math.ceil(chars.length / count)
    pieces = []
    for (let i = 0; i < chars.length; i += size) pieces.push(chars.slice(i, i + size).join(''))
    pieces = pieces.map((piece, i) => i < pieces.length - 1 ? piece + '-' : piece)
    count++
  } while (pieces.some(piece => textWidthEm(piece) > maxEm))
  return pieces
}

const truncateToFit = (line: string, maxEm: number) => {
  let chars = Array.from(line)
  while (chars.length > 1 && textWidthEm(chars.join('').trimEnd() + '…') > maxEm) chars = chars.slice(0, -1)
  return chars.join('').trimEnd() + '…'
}

// Plan B para nombres muy largos: envolver a tamaño mínimo y recortar con «…».
const wrapAtMinSize = (words: string[]): string[] => {
  const maxEm = LOGO_MAX_TEXT_WIDTH / LOGO_MIN_FONT_SIZE
  const tokens = words.flatMap(word => textWidthEm(word) > maxEm ? hyphenate(word, maxEm) : [word])
  const lines: string[] = []
  for (const token of tokens) {
    const last = lines[lines.length - 1]
    const joiner = last?.endsWith('-') ? '' : ' '
    if (last !== undefined && textWidthEm(last + joiner + token) <= maxEm) lines[lines.length - 1] = last + joiner + token
    else lines.push(token)
  }
  if (lines.length <= LOGO_MAX_LINES) return lines
  const kept = lines.slice(0, LOGO_MAX_LINES)
  kept[LOGO_MAX_LINES - 1] = truncateToFit(kept[LOGO_MAX_LINES - 1].replace(/-$/, '') + ' ' + lines[LOGO_MAX_LINES], maxEm)
  return kept
}

export const layoutLogoName = (name?: string | null): LogoNameLayout => {
  const words = normalizeLogoName(name).split(' ')

  let lines: string[] = []
  let fontSize = 0
  for (let n = 1; n <= Math.min(LOGO_MAX_LINES, words.length); n++) {
    const candidate = balancedSplit(words, n)
    const size = fontSizeFor(candidate)
    // Menos líneas salvo que partir agrande la letra de verdad (≥15 %): una
    // línea luce más en la cabecera apaisada del menú.
    if (size > fontSize * 1.15) {
      lines = candidate
      fontSize = size
    }
  }

  if (fontSize < LOGO_MIN_FONT_SIZE) {
    lines = wrapAtMinSize(words)
    fontSize = fontSizeFor(lines)
  }

  fontSize = Math.floor(fontSize)
  return {
    lines,
    fontSize,
    lineHeight: Math.round(fontSize * LOGO_LINE_HEIGHT),
    lineWidths: lines.map(line => Math.min(LOGO_MAX_TEXT_WIDTH, Math.round(textWidthEm(line) * fontSize)))
  }
}

const INITIALS_STOPWORDS = new Set([
  'BAR', 'EL', 'LA', 'LOS', 'LAS', 'DE', 'DEL', 'Y', 'THE', 'AND', 'OF',
  'RESTAURANTE', 'RESTAURANT', 'CAFE', 'CAFÉ', 'CAFETERIA', 'CAFETERÍA'
])

const firstAlphanumeric = (word: string) => Array.from(word).find(char => /[\p{L}\p{N}]/u.test(char))

export const getLogoInitials = (name?: string | null) => {
  const words = normalizeLogoName(name).split(' ').filter(word => firstAlphanumeric(word))
  const significant = words.filter(word => !INITIALS_STOPWORDS.has(word))
  const source = significant.length > 0 ? significant : words
  const initials = source.slice(0, 2).map(firstAlphanumeric).join('')
  return initials || firstAlphanumeric(LOGO_FALLBACK_NAME)!
}
